"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ClipboardCheck, Cpu, FileCheck2, GraduationCap, ShieldCheck, Clock, ArrowRight, CircleCheck as CheckCircle2, CalendarCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const phases = [{
  step: "01",
  icon: ClipboardCheck,
  title: "Application Assessment & URS Scoping",
  duration: "1 - 2 Weeks",
  description: "Detailed evaluation of your laboratory workflow, sample matrices, analytical requirements, and regulatory criteria to develop a tight User Requirement Specification."
}, {
  step: "02",
  icon: Cpu,
  title: "Technology Selection & System Design",
  duration: "2 - 3 Weeks",
  description: "Configuring the analytical hardware, optical probes, software interfaces, and qualification documentation to match cleanroom and compliance standards."
}, {
  step: "03",
  icon: FileCheck2,
  title: "IQ/OQ Qualification & Site Deployment",
  duration: "1 - 2 Weeks",
  description: "Delivery, physical installation, and rigorous execution of factory-calibrated IQ/OQ qualification protocols following documented qualification protocols."
}, {
  step: "04",
  icon: GraduationCap,
  title: "Method Validation & Technical Training",
  duration: "1 - 2 Weeks",
  description: "On-site operator certification, chemometric model calibration or method transfer, and complete standard operating procedure (SOP) verification."
}, {
  step: "05",
  icon: ShieldCheck,
  title: "Lifecycle Support & Routine Audits",
  duration: "Ongoing / Annual SLA",
  description: "Scheduled preventive maintenance, emergency rapid-response support across Ireland and the UK, and annual requalification to secure continuous uptime."
}];
const timelineHighlights = [{
  label: "Average Project Turnaround",
  value: "4 - 8 Weeks",
  subtext: "From technical scoping to final handover"
}, {
  label: "Protocol Standard",
  value: "Structured Protocol Execution",
  subtext: "Complete audit-ready documentation"
}, {
  label: "Regional Support Coverage",
  value: "Ireland & UK",
  subtext: "Dedicated field engineering teams"
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="consultationmethodology" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4">
            Structured Deployment
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-semibold tracking-tight text-foreground leading-[1.15]">
            Consultation methodology and project timeline
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Our step-by-step engineering model is designed for regulated laboratories, pharmaceutical manufacturing, and quality assurance teams needing zero downtime and fully compliant implementation.
          </p>
        </div>

        {/* Phase Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {phases.map((phase, i) => {
          const Icon = phase.icon;
          return <motion.div key={phase.step} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-mono font-bold tracking-widest text-primary uppercase bg-secondary px-2.5 py-1 rounded-md">
                        Phase {phase.step}
                      </span>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium bg-muted px-2.5 py-1 rounded-md">
                        <Clock className="size-3.5 text-primary" />
                        <span>{phase.duration}</span>
                      </div>
                    </div>
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground mb-2">
                      <Icon className="size-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {phase.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {phase.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}

          {/* Project Summary Callout Card */}
          <motion.div data-index={5} variants={itemVariants} className="h-full md:col-span-2 lg:col-span-1">
            <Card className="h-full border border-primary/30 bg-secondary/50 shadow-sm flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                  <CalendarCheck className="size-4" />
                  <span>Timeline Predictability</span>
                </div>
                <CardTitle className="text-xl font-semibold text-secondary-foreground">
                  Need an expedited installation?
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  We offer fast-track qualification packages for high-priority production lines and audit deadlines.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-2.5 mb-6 text-sm text-foreground">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Pre-configured test instruments available</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Direct engineering support across Ireland and UK</span>
                  </div>
                </div>
                <Button asChild className="w-full justify-between">
                  <Link to="/ContactPage">
                    <span>Discuss Your Timeline</span>
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Key Metrics / Highlights Bar */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {timelineHighlights.map((stat, index) => <div key={stat.label} data-index={index} className={`flex flex-col ${index !== 0 ? "sm:pl-6 pt-4 sm:pt-0" : ""}`}>
                <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground mb-1">
                  {stat.label}
                </span>
                <span className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                  {stat.value}
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  {stat.subtext}
                </span>
              </div>)}
          </div>
        </div>

        {/* Bottom CTA Helper */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-lg bg-muted/40 border border-border">
          <div className="text-center sm:text-left">
            <h3 className="text-base font-semibold text-foreground">
              Ready to scope an analytical instrumentation project?
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Review custom qualification plans tailored to your facility requirements.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="sm">
              <Link to="/ServicesPage">Explore Services</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/ContactPage">Request Consultation</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}